import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Redirect } from "react-router-dom";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import { Elements, AddressElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Loader from "../../../../components/Loader";

const OnboardingClient = () => {
  const theme = useTheme();
  const stripePromise = useMemo(
    () => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY),
    []
  );

  const [loading, setLoading] = useState(true);
  const [redirectHome, setRedirectHome] = useState(false);
  const [inSubmit, setInSubmit] = useState(false);
  const [step, setStep] = useState(0);
  const [accountMode, setAccountMode] = useState("create");
  const [message, setMessage] = useState({ type: "", text: "" });

  const [companyName, setCompanyName] = useState("");
  const [billingEmail, setBillingEmail] = useState(FirebaseAuth.auth().currentUser?.email || "");

  const [complete, setComplete] = useState(false);
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });
  const [phone, setPhone] = useState("");

  const [taxId, setTaxId] = useState("");
  const [joinAccountId, setJoinAccountId] = useState("");
  const [invitePreview, setInvitePreview] = useState(null);
  const [joinSubmitting, setJoinSubmitting] = useState(false);

  const canSubmit = !!companyName.trim() && !!billingEmail.trim() && complete;
  const isCreateMode = accountMode === "create";
  const setupSteps = ["Business", "Tax", "Review"];

  useEffect(() => {
    let mounted = true;

    const checkExistingWorkspace = async () => {
      const uid = FirebaseAuth.auth().currentUser?.uid;
      if (!uid) {
        if (mounted) setLoading(false);
        return;
      }

      try {
        const snap = await FirebaseAuth.firestore().collection("users").doc(uid).get();
        const data = snap.exists ? snap.data() || {} : {};
        const hasActiveClientAccount = !!String(data.active_account_id || "").trim();
        const hasClientAccountIds = Array.isArray(data.account_ids) && data.account_ids.length > 0;
        if (hasActiveClientAccount && hasClientAccountIds) {
          if (mounted) setRedirectHome(true);
          return;
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkExistingWorkspace();

    return () => {
      mounted = false;
    };
  }, []);

  const handleModeChange = (_event, nextMode) => {
    if (!nextMode) return;
    setAccountMode(nextMode);
    setStep(0);
    setMessage({ type: "", text: "" });
  };

  const handleSubmit = async () => {
    setInSubmit(true);
    setLoading(true);
    setMessage({ type: "", text: "" });

    const uid = FirebaseAuth.auth().currentUser?.uid;
    if (!uid) {
      setInSubmit(false);
      setLoading(false);
      setMessage({ type: "error", text: "User session is missing. Please sign in again." });
      return;
    }

    try {
      const createClientAccount = FirebaseAuth.functions().httpsCallable("createClientAccount");
      await createClientAccount({
        uid,
        company_name: companyName.trim(),
        billing_email: billingEmail.trim(),
        fname: contactFirstName.trim(),
        lname: contactLastName.trim(),
        contact_name: `${contactFirstName} ${contactLastName}`.trim(),
        address: {
          line1: address.line1 || "",
          line2: address.line2 || "",
          city: address.city || "",
          state: address.state || "",
          postal_code: address.postal_code || "",
          country: (address.country || "US").toUpperCase(),
        },
        phone: phone.trim(),
        tax_id: taxId.trim(),
      });

      setTimeout(() => {
        window.location.reload();
      }, 900);
    } catch (error) {
      setInSubmit(false);
      setLoading(false);
      setMessage({ type: "error", text: error?.message || "Failed to submit onboarding." });
    }
  };

  const handleJoinRequest = async () => {
    const inviteToken = String(joinAccountId || "").trim();
    if (!inviteToken) {
      setMessage({ type: "error", text: "Enter the invite token shared by your account admin." });
      return;
    }
    setJoinSubmitting(true);
    setMessage({ type: "", text: "" });
    setInvitePreview(null);
    try {
      const previewInvite = FirebaseAuth.functions().httpsCallable("previewClientAccountInvite");
      const previewResponse = await previewInvite({ token: inviteToken });
      const previewData = previewResponse?.data || null;
      setInvitePreview(previewData);

      const redeemInvite = FirebaseAuth.functions().httpsCallable("redeemClientAccountInvite");
      await redeemInvite({
        token: inviteToken,
      });
      setMessage({ type: "success", text: "Invite redeemed successfully. Reloading dashboard..." });
      setTimeout(() => {
        window.location.reload();
      }, 1200);
    } catch (error) {
      setMessage({ type: "error", text: error?.message || "Failed to redeem invite." });
    } finally {
      setJoinSubmitting(false);
    }
  };

  return (
    <>
      {redirectHome && <Redirect to="/" />}
      <Loader variant="fullscreen" open={loading} label={inSubmit ? "Creating client workspace..." : "Preparing client setup..."} />

      {!loading && !redirectHome && <Container sx={{ py: { xs: 2.5, md: 4 }, px: { xs: 2, sm: 3 }, maxWidth: "980px !important" }}>
        <Paper
          variant="outlined"
          sx={{
            px: 0,
            py: 0,
            width: "100%",
            borderWidth: 1,
            borderRadius: 4,
            backgroundColor: theme.palette.background.paper,
            boxShadow: "0 18px 60px rgba(60,64,67,0.10)",
            overflow: "hidden",
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 5,
              background: "linear-gradient(90deg, #1976D2, #05690D)",
            },
          }}
        >
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Box sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 3, md: 4 }, pb: 1, textAlign: "left" }}>
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 850, letterSpacing: 0.9, textTransform: "uppercase" }}>
                Client setup
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 850, color: "#30343b", letterSpacing: "-0.03em", mt: 0.75 }}>
                Set up your client workspace
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720, lineHeight: 1.6 }}>
                Create a billing account or accept an invitation so your team can request hires, approve work, and manage invoices.
              </Typography>
              {isCreateMode && (
                <Stepper activeStep={step} alternativeLabel sx={{ mt: 3, display: { xs: "none", sm: "flex" } }}>
                  {setupSteps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
            </Box>
            <Paper
              role="dialog"
              aria-modal="false"
              aria-label="header"
              square
              variant="outlined"
              tabIndex={-1}
              sx={{
                position: "relative",
                bottom: 0,
                left: 0,
                right: 0,
                m: 0,
                p: { xs: 2.5, md: 4 },
                borderWidth: 0,
                backgroundColor: "transparent",
              }}
            >
              {message.text && (
                <Alert severity={message.type || "info"} sx={{ mb: 2, textAlign: "left" }}>
                  {message.text}
                </Alert>
              )}

              <Paper
                variant="outlined"
                sx={{
                  mb: 3,
                  px: 2,
                  py: 2,
                  border: "1px solid #e3e7ee",
                  borderRadius: 3,
                  backgroundColor: "#f8fafd",
                  textAlign: "center",
                }}
              >
                <ToggleButtonGroup
                  value={accountMode}
                  exclusive
                  onChange={handleModeChange}
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 800,
                      py: 1,
                      borderRadius: 2,
                    },
                  }}
                >
                  <ToggleButton value="create">Create a new account</ToggleButton>
                  <ToggleButton value="join">Join an existing account</ToggleButton>
                </ToggleButtonGroup>
              </Paper>

              {step === 0 && isCreateMode && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", gap: 2, pb: 3 }}>
                    <Box sx={{ flexShrink: 1, textAlign: "start" }}>
                      <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                        Business account
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
                        Add the company and primary billing contact that will appear on invoices and approvals.
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Company Name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Billing Email"
                        type="email"
                        value={billingEmail}
                        onChange={(e) => setBillingEmail(e.target.value)}
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 1, textAlign: "left", width: "100%", overflow: "hidden" }}>
                    <Box sx={{ width: "100%", "& iframe": { maxWidth: "100%" } }}>
                      <Elements stripe={stripePromise}>
                        <AddressElement
                          options={{
                            mode: "shipping",
                            allowedCountries: ["US", "CA", "IN"],
                            fields: {
                              phone: "always",
                            },
                            validation: {
                              phone: {
                                required: "always",
                              },
                            },
                            display: {
                              name: "split",
                            },
                            defaultValues: {
                              firstName: contactFirstName,
                              lastName: contactLastName,
                              address,
                              phone,
                            },
                          }}
                          onChange={(event) => {
                            setComplete(event.complete);
                            if (event.value) {
                              setContactFirstName(event.value.firstName || "");
                              setContactLastName(event.value.lastName || "");
                              setAddress(event.value.address || {});
                              setPhone(event.value.phone || "");
                            }
                          }}
                        />
                      </Elements>
                    </Box>
                  </Box>
                </>
              )}

              {step === 0 && !isCreateMode && (
                <Stack direction="column" sx={{ justifyContent: "space-between", gap: 2, pb: 3 }}>
                  <Box sx={{ flexShrink: 1, textAlign: "left" }}>
                    <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                      Join with invite
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
                      Paste the invite token shared by your account admin to join an existing client workspace.
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        label="Invite token"
                        value={joinAccountId}
                        onChange={(e) => {
                          setJoinAccountId(e.target.value);
                          setInvitePreview(null);
                        }}
                        fullWidth
                        size="small"
                        helperText="Paste invite token received from your account admin"
                      />
                    </Grid>
                  </Grid>
                  <Stack direction="row" sx={{ justifyContent: "flex-end"}}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={joinSubmitting || !String(joinAccountId || "").trim()}
                      sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
                      onClick={handleJoinRequest}
                    >
                      {joinSubmitting ? "Accepting..." : "Accept Invite"}
                    </Button>
                  </Stack>
                  {invitePreview && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Invite Preview
                        </Typography>
                        <Typography variant="body2">
                          Account: {invitePreview.account_company_name || invitePreview.account_name || invitePreview.account_id}
                        </Typography>
                        <Typography variant="body2">
                          Account ID: {invitePreview.account_id}
                        </Typography>
                        {invitePreview.inviter_name && (
                          <Typography variant="body2">
                            Invited by: {invitePreview.inviter_name}
                          </Typography>
                        )}
                        {invitePreview.invited_email && (
                          <Typography variant="body2">
                            Invite email lock: {invitePreview.invited_email}
                          </Typography>
                        )}
                        {invitePreview.expires_at && (
                          <Typography variant="body2">
                            Expires at: {invitePreview.expires_at}
                          </Typography>
                        )}
                      </Paper>
                    </>
                  )}
                </Stack>
              )}

              {step === 1 && isCreateMode && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", gap: 2, pb: 3 }}>
                    <Box sx={{ flexShrink: 1, textAlign: "start" }}>
                      <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                        Tax details
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
                        Add optional tax information for billing records. You can update these details later.
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Tax ID (Optional)"
                        value={taxId}
                        onChange={(e) => setTaxId(e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {step === 2 && isCreateMode && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", gap: 2, pb: 3 }}>
                    <Box sx={{ flexShrink: 1, textAlign: "start" }}>
                      <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                        Final review
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 680 }}>
                        Check the account details before submitting your client workspace setup.
                      </Typography>
                    </Box>
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                    <Stack spacing={1.25} sx={{ textAlign: 'left' }}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Business Information</Typography>
                        <Stack spacing={0.4}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{companyName || "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">{billingEmail || "-"}</Typography>
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Primary Contact</Typography>
                        <Stack spacing={0.4}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{`${contactFirstName} ${contactLastName}`.trim() || "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">{address.line1 || "-"}</Typography>
                          {address.line2 ? <Typography variant="body2" color="text.secondary">{address.line2}</Typography> : null}
                          <Typography variant="body2" color="text.secondary">
                            {[address.city, address.state, address.postal_code, address.country].filter(Boolean).join(", ") || "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{phone || "-"}</Typography>
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Tax Information</Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Tax ID</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{taxId || "Not provided"}</Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Paper>
                </>
              )}

              {isCreateMode && (
                <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", gap: 2, direction: "rtl", pt: 3 }}>
                  <Stack
                    direction={{ xs: "row-reverse", sm: "row" }}
                    sx={{ gap: 2, flexShrink: 0, alignSelf: { xs: "flex-end", sm: "center" } }}
                  >
                    {step >= 0 && step < 2 && (
                      <Button
                        size="small"
                        disabled={(step === 0 && !canSubmit) || inSubmit}
                        variant="contained"
                        sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
                        onClick={() => setStep(step + 1)}
                      >
                        Next
                      </Button>
                    )}
                    {step === 2 && (
                      <Button
                        size="small"
                        disabled={inSubmit || !canSubmit}
                        variant="contained"
                        sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
                        onClick={handleSubmit}
                      >
                        {inSubmit ? <Loader /> : "Submit"}
                      </Button>
                    )}
                    {step > 0 && step <= 2 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        sx={{ textTransform: "none", borderColor: theme.palette.grey[300], borderRadius: 2 }}
                        onClick={() => setStep(step - 1)}
                      >
                        Back
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
            </Paper>
          </Box>
        </Paper>
      </Container>}
    </>
  );
};

export default OnboardingClient;
